/**
 * BumperBattlePanel — iter-119o
 *
 * Lives inside both PlyxLiveNew (host) and PlyxLiveViewer (viewer)
 * rooms. Renders:
 *
 *   HOST + bumper inactive  →  "Start Bumper Challenge" CTA + picker modal
 *   HOST + bumper active    →  Standings tile + "End Bumper" button
 *   VIEWER + bumper active  →  Standings tile + tap-to-gift any participant
 *   VIEWER + bumper inactive → null (no UI)
 *
 * Real-time updates flow via the existing plyx_hub WS:
 *   bumper.start  → reveal panel, seed participants
 *   bumper.update → recompute standings (after every gift)
 *   bumper.end    → show winner modal for 6s then collapse
 */
import React, { useEffect, useMemo, useState } from "react";
import { useRoomContext, useParticipants, useLocalParticipant } from "@livekit/components-react";
import { Crown, Coins, Loader2, X, Swords, Trophy } from "lucide-react";
import { toast } from "sonner";
import { plyxApi, plyxMediaUrl } from "@/lib/plyxApi";

const formatCoins = (n) => {
  const c = Number(n) || 0;
  if (c >= 1000) return `${(c / 1000).toFixed(c >= 10000 ? 0 : 1)}k`;
  return `${c}`;
};

const BumperBattlePanel = ({
  roomName,
  isHost,
  hostId,
  onGiftParticipant, // (recipient_user_id) => void — opens gift sheet pre-targeted
}) => {
  const room = useRoomContext();
  const participants = useParticipants();
  const { localParticipant } = useLocalParticipant();
  // Stage-guest IDs = every remote participant other than the local
  // user. Used for the host's "Start Bumper" picker. Recomputes
  // whenever LiveKit signals a roster change so the picker always
  // shows who's actually live on stage.
  const stageGuestIds = useMemo(() => participants
    .filter((p) => p && p.identity && (!localParticipant || p.identity !== localParticipant.identity))
    .map((p) => p.identity), [participants, localParticipant]);
  const [state, setState] = useState({
    active: false,
    participants: [], // [{user_id, handle, display_name, avatar_url, coins}]
    winnerId: null,
    winnerMeta: null,
    showWinner: false,
  });
  const [pickerOpen, setPickerOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pickedIds, setPickedIds] = useState(() => new Set([hostId]));
  const [guestMeta, setGuestMeta] = useState({}); // user_id -> {handle, display_name, avatar_url}

  // Bootstrap: pull current standings on mount so a late-joining viewer
  // immediately sees an in-progress battle.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await plyxApi.get(`/plyx/live/${roomName}/bumper/standings`);
        if (cancelled) return;
        if (data?.active) {
          setState({
            active: true,
            participants: (data.standings || []).map((s) => ({
              user_id: s.user_id, handle: s.handle, display_name: s.display_name, avatar_url: s.avatar_url, coins: s.coins,
            })),
            winnerId: null, winnerMeta: null, showWinner: false,
          });
        }
      } catch { /* not started yet — fine */ }
    })();
    return () => { cancelled = true; };
  }, [roomName]);

  // Hydrate the host's stage-guest picker with display names so the
  // checkboxes show "@handle" not raw IDs. We extract from LiveKit
  // participant metadata (which includes name + handle when set at
  // token-mint time). For ids without rich metadata we fall back to
  // the user_id, which is still tappable.
  useEffect(() => {
    if (!isHost || !pickerOpen) return;
    const meta = {};
    for (const p of participants) {
      let parsed = {};
      try { if (p.metadata) parsed = JSON.parse(p.metadata) || {}; } catch { /* not JSON */ }
      meta[p.identity] = {
        handle: parsed.handle,
        display_name: parsed.display_name || p.name || parsed.handle,
        avatar_url: parsed.avatar_url,
      };
    }
    setGuestMeta(meta);
  }, [isHost, pickerOpen, participants]);

  // Subscribe to bumper events from the plyx_hub WS (piggy-backed on
  // LiveKit's text-data channel which is already plumbed through
  // EngagementLayer in PlyxLiveViewer; for the host page we hook the
  // global ws via plyxApi defaults — see plyx_realtime.py).
  useEffect(() => {
    const handler = (ev) => {
      const msg = ev?.detail;
      if (!msg || typeof msg !== "object") return;
      if (msg.type === "bumper.start") {
        setState({
          active: true,
          participants: (msg.participants || []).map((p) => ({
            user_id: p.plyx_user_id, handle: p.handle, display_name: p.display_name, avatar_url: p.avatar_url, coins: 0,
          })),
          winnerId: null, winnerMeta: null, showWinner: false,
        });
        if (!isHost) toast("⚔️ Bumper Challenge started — gift your favourite!");
      } else if (msg.type === "bumper.update") {
        const byUser = msg.coins_by_user || {};
        setState((s) => ({
          ...s,
          participants: s.participants
            .map((p) => ({ ...p, coins: Number(byUser[p.user_id] || 0) }))
            .sort((a, b) => b.coins - a.coins),
        }));
      } else if (msg.type === "bumper.end") {
        setState((s) => ({
          ...s,
          active: false,
          winnerId: msg.winner_user_id,
          winnerMeta: msg.winner,
          showWinner: true,
          participants: s.participants
            .map((p) => ({ ...p, coins: Number((msg.coins_by_user || {})[p.user_id] || p.coins) }))
            .sort((a, b) => b.coins - a.coins),
        }));
        setTimeout(() => setState((s) => ({ ...s, showWinner: false })), 6500);
      }
    };
    window.addEventListener("plyx-live-message", handler);
    return () => window.removeEventListener("plyx-live-message", handler);
  }, [isHost]);

  // ALSO listen to the LiveKit data channel for the same events — the
  // backend broadcasts to plyx_hub but viewers in this React tree only
  // receive WS via the listener above when EngagementLayer has bridged
  // it. The room object's `dataReceived` covers the gap when the WS is
  // briefly dropped.
  useEffect(() => {
    if (!room) return;
    const onData = (payload) => {
      try {
        const text = new TextDecoder().decode(payload);
        const msg = JSON.parse(text);
        if (msg.type === "bumper.start" || msg.type === "bumper.update" || msg.type === "bumper.end") {
          window.dispatchEvent(new CustomEvent("plyx-live-message", { detail: msg }));
        }
      } catch { /* not JSON */ }
    };
    room.on("dataReceived", onData);
    return () => { try { room.off("dataReceived", onData); } catch { /* ignore */ } };
  }, [room]);

  const startBumper = async () => {
    if (pickedIds.size < 2) {
      toast.error("Pick at least one challenger besides yourself.");
      return;
    }
    setBusy(true);
    try {
      await plyxApi.post(`/plyx/live/${roomName}/bumper/start`, {
        participants: Array.from(pickedIds),
      });
      setPickerOpen(false);
      toast.success("Bumper Challenge live!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start the challenge.");
    } finally {
      setBusy(false);
    }
  };

  const endBumper = async () => {
    if (!window.confirm("End the Bumper Challenge now? The current leader wins.")) return;
    setBusy(true);
    try {
      await plyxApi.post(`/plyx/live/${roomName}/bumper/end`, {});
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not end the challenge.");
    } finally {
      setBusy(false);
    }
  };

  const leaderCoins = useMemo(
    () => state.participants.reduce((m, p) => Math.max(m, p.coins || 0), 0),
    [state.participants],
  );

  // ─── HOST: idle CTA ───────────────────────────────────────────────
  if (isHost && !state.active) {
    return (
      <>
        <button
          type="button"
          onClick={() => { setPickedIds(new Set([hostId])); setPickerOpen(true); }}
          data-testid="plyx-bumper-start-cta"
          className="absolute top-3 right-[150px] z-[55] inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-gradient-to-r from-[#FF2D7A] via-[#FFB42D] to-[#00E0C7] text-black font-bold text-xs shadow-xl"
          title="Start a live gift battle with on-stage guests"
        >
          <Swords className="w-3.5 h-3.5"/> Bumper
        </button>
        {pickerOpen && (
          <div className="fixed inset-0 z-[80] bg-black/85 backdrop-blur-sm flex items-end sm:items-center justify-center p-3" onClick={() => !busy && setPickerOpen(false)}>
            <div className="w-full max-w-md bg-[#0A0A0A] border border-white/10 rounded-3xl p-5" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-display text-xl inline-flex items-center gap-2"><Swords className="w-5 h-5 text-[#FF2D7A]"/> Start a Bumper Challenge</h3>
                  <p className="text-xs text-white/60 mt-1">Pick on-stage guests to battle with. Viewers gift their favourite; highest coin count wins.</p>
                </div>
                <button type="button" onClick={() => setPickerOpen(false)} className="w-8 h-8 rounded-full bg-white/10"><X className="w-4 h-4 mx-auto"/></button>
              </div>
              {(stageGuestIds || []).length === 0 ? (
                <div className="py-6 text-center text-white/60 text-sm">
                  No on-stage guests yet. Tap <strong>Invite to stage</strong> first, then come back to start the battle.
                </div>
              ) : (
                <div className="space-y-2 max-h-[40vh] overflow-y-auto">
                  <div className="px-3 py-2 rounded-xl bg-[#FFB42D]/10 border border-[#FFB42D]/30 text-xs font-semibold inline-flex items-center gap-2">
                    <Crown className="w-3.5 h-3.5 text-[#FFB42D]"/> You (host) — auto-included
                  </div>
                  {(stageGuestIds || []).map((uid) => {
                    const m = guestMeta[uid] || {};
                    const picked = pickedIds.has(uid);
                    return (
                      <button
                        key={uid}
                        type="button"
                        onClick={() => setPickedIds((prev) => {
                          const next = new Set(prev);
                          if (next.has(uid)) next.delete(uid); else next.add(uid);
                          return next;
                        })}
                        data-testid={`plyx-bumper-pick-${uid}`}
                        className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl border-2 transition ${picked ? "border-[#FF2D7A] bg-gradient-to-r from-[#FF2D7A] via-[#FFB42D] to-[#00E0C7]/10" : "border-white/10 bg-white/5"}`}
                      >
                        {m.avatar_url
                          ? <img src={plyxMediaUrl(m.avatar_url)} alt="" className="w-8 h-8 rounded-full object-cover"/>
                          : <div className="w-8 h-8 rounded-full bg-white/10"/>}
                        <div className="text-left flex-1 min-w-0">
                          <div className="font-semibold text-sm truncate">{m.display_name || m.handle || uid}</div>
                          {m.handle && <div className="text-[10px] text-white/50">@{m.handle}</div>}
                        </div>
                        {picked && <span className="text-[10px] font-bold text-[#FF2D7A]">PICKED</span>}
                      </button>
                    );
                  })}
                </div>
              )}
              <button
                type="button"
                onClick={startBumper}
                disabled={busy || pickedIds.size < 2}
                data-testid="plyx-bumper-start-confirm"
                className="mt-4 w-full px-4 py-3 rounded-2xl font-bold text-black text-sm shadow-lg disabled:opacity-50"
                style={{ background: "linear-gradient(135deg, #FF2D7A 0%, #FFB42D 100%)" }}
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin mx-auto"/> : `Start with ${pickedIds.size} fighter${pickedIds.size === 1 ? "" : "s"} ⚔️`}
              </button>
            </div>
          </div>
        )}
        {/* Winner banner persists for 6s after `bumper.end`. */}
        {state.showWinner && <WinnerBanner meta={state.winnerMeta} coins={state.participants[0]?.coins || 0}/>}
      </>
    );
  }

  // ─── ACTIVE: live standings tile (host + viewer) ──────────────────
  if (!state.active && !state.showWinner) return null;

  return (
    <>
      <div
        data-testid="plyx-bumper-standings"
        className="absolute top-16 right-3 z-[55] w-[min(220px,calc(100vw-1.5rem))] bg-black/75 backdrop-blur-md border border-white/15 rounded-2xl p-2 shadow-xl"
      >
        <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#FF2D7A] mb-2 inline-flex items-center gap-1">
          <Swords className="w-3 h-3"/> Bumper
        </div>
        <div className="space-y-1.5">
          {state.participants.map((p, idx) => {
            const isLeader = leaderCoins > 0 && p.coins === leaderCoins;
            return (
              <button
                key={p.user_id}
                type="button"
                disabled={isHost || !onGiftParticipant}
                onClick={() => onGiftParticipant?.(p.user_id)}
                data-testid={`plyx-bumper-row-${p.user_id}`}
                className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-left transition ${
                  isHost ? "" : "hover:bg-white/10 active:bg-white/15"
                } ${isLeader ? "bg-gradient-to-r from-[#FFD700]/25 to-transparent" : ""}`}
                title={isHost ? `${p.coins} coins` : `Tap to gift @${p.handle}`}
              >
                <div className="relative">
                  {p.avatar_url
                    ? <img src={plyxMediaUrl(p.avatar_url)} alt="" className="w-7 h-7 rounded-full object-cover"/>
                    : <div className="w-7 h-7 rounded-full bg-white/10"/>}
                  {isLeader && (
                    <Crown className="w-3 h-3 text-[#FFD700] absolute -top-1.5 -right-1.5" fill="currentColor"/>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold text-white truncate">@{p.handle || "—"}</div>
                  <div className="text-[10px] text-white/55">#{idx + 1}</div>
                </div>
                <div className="inline-flex items-center gap-0.5 text-[#FFB42D] font-bold text-xs">
                  <Coins className="w-3 h-3"/> {formatCoins(p.coins)}
                </div>
              </button>
            );
          })}
        </div>
        {isHost && state.active && (
          <button
            type="button"
            onClick={endBumper}
            disabled={busy}
            data-testid="plyx-bumper-end-btn"
            className="mt-2 w-full px-2.5 py-1.5 rounded-lg bg-white/10 hover:bg-white/15 text-[11px] font-bold text-white inline-flex items-center justify-center gap-1"
          >
            {busy ? <Loader2 className="w-3 h-3 animate-spin"/> : <>End battle <Trophy className="w-3 h-3"/></>}
          </button>
        )}
        {!isHost && state.active && (
          <p className="mt-2 text-[10px] text-white/55 text-center">Tap a fighter to gift them</p>
        )}
      </div>
      {state.showWinner && <WinnerBanner meta={state.winnerMeta} coins={state.participants[0]?.coins || 0}/>}
    </>
  );
};

const WinnerBanner = ({ meta, coins }) => (
  <div
    data-testid="plyx-bumper-winner-banner"
    className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[70] px-6 py-5 rounded-3xl bg-gradient-to-br from-[#FFD700] via-[#FFB42D] to-[#FF2D7A] text-black text-center shadow-2xl border-2 border-white/30 animate-pulse"
  >
    <div className="flex items-center justify-center gap-2 mb-2">
      <Trophy className="w-6 h-6"/>
      <div className="font-display text-xl font-bold">BUMPER WINNER</div>
      <Trophy className="w-6 h-6"/>
    </div>
    {meta?.avatar_url
      ? <img src={plyxMediaUrl(meta.avatar_url)} alt="" className="w-16 h-16 rounded-full mx-auto mb-1 border-2 border-black object-cover"/>
      : <div className="w-16 h-16 rounded-full bg-black/20 mx-auto mb-1 border-2 border-black"/>}
    <div className="text-base font-bold">@{meta?.handle || "—"}</div>
    <div className="text-xs font-semibold inline-flex items-center gap-1 mt-1">
      <Coins className="w-3.5 h-3.5"/> {coins.toLocaleString()} coins
    </div>
  </div>
);

export default BumperBattlePanel;
